import { DEFAULT_GOOGLE_DRIVE_REQUEST_DATA } from "constants/widgetBuilder";
import _ from "lodash";
import PropTypes from "prop-types";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { useUploadProgress } from "services/customHook";
import { getUrlS3 } from "services/funcHelper";
import {
	convertURL,
	convertUploadPayload,
	getTermsIds,
	handleFileError,
} from "services/widgetBuilder";
import {
	DEFAULT_PAYLOAD,
	DURATION,
	FAILED,
	IN_PROGRESS,
	LIMITATION_ERROR_TYPE,
	MAX_PERCENT,
	NONE,
	SCRIPT_TAG,
	SUCCEEDED,
	UPLOADER_TYPE,
} from "../../../constants/common";
import THEME from "../../../constants/themes";
import WidgetBuilderContext from "../../../contexts/WidgetBuilder";
import utils from "../../../services/utils";
import {
	cancelRequest,
	sendEmailRequest,
	setUploadProgress,
	storeData,
	storeGoogleDataRequest,
	uploadContentRequest,
} from "../../../stores/actions";
import {
	StyledDetailWidget,
	StyledWrapperDetailWidget,
} from "../../../styled-components/WidgetBuilder/WidgetBuilderStyled";
import NotFound from "../../pages/NotFound/NotFound";
import useScrollHandler from "../../shared/ScrollHandler";
import Default from "../Default/Default";
import CancelUpload from "../UploadProgress/CancelUpload";
import UploadProgress from "../UploadProgress/UploadProgress";
import WidgetInstagram from "./Instagram/WidgetInstagram";
import WidgetTikTok from "./TikTok/WidgetTikTok";
import UploadLimitTerms from "./UploadLimitTerms";
import UploaderConfirmation from "./UploaderConfirmation";
import WidgetBody from "./WidgetBody";
import WidgetFooter from "./WidgetFooter";
import WidgetHeader from "./WidgetHeader";
import WidgetYouTube from "./YouTube/WidgetYouTube";

const WidgetBuilder = (props) => {
	const wrapperRef = useRef();
	const isMobile = utils.useDevice();
	const dispatch = useDispatch();
	const { t } = useTranslation();

	const { globalProps, widgetInfo } = props;
	const { requestAPI, isShowLoading, setIsShowLoading } = globalProps;
	const {
		fileProgress,
		uploadedContentListResult,
		createContentStatus,
		createdContent,
		clientDetail,
		getClientDetailStatus,
		isCancelledProgressModal = false,
		uploadSocialContentResult,
		uploadSocialContentStatus,
		uploadSocialContentError,
		storedCode,
		uploadSourceType,
	} = useSelector((state) => state.widgets);
	const { error: googleApiError } = useSelector((state) => state.googleStore);

	const [isMoreField, setIsMoreField] = useState(false);
	const [isFailed, setIsFailed] = useState();
	const [formState, setFormState] = useState(1); // 1 Upload Page, 2 Thank You Page
	const [uploadPayloadList, setUploadPayloadList] = useState([]);
	const [uploadResults, setUploadResults] = useState([]);
	const [errorMsg, setErrorMsg] = useState("");

	const [payload, setPayload] = useState({ ...DEFAULT_PAYLOAD });
	const [videoList, setVideoList] = useState(null);
	const [selectedVideoList, setSelectedVideoList] = useState([]);
	const [tikTokUser, setTikTokUser] = useState(null);
	const [activeUploaderType, setActiveUploaderType] = useState(null);

	const [isCancelUpload, setIsCancelUpload] = useState(false);
	const [isUploadLimitTerms, setIsUploadLimitTerms] = useState(false);
	const [googleAppData, setGoogleAppData] = useState(DEFAULT_GOOGLE_DRIVE_REQUEST_DATA);
	const [modalType, setModalType] = useState();
	const [terms, setTerms] = useState([]);
	const [isDisabledSubmitButton, setIsDisabledSubmitButton] = useState(false);

	const pathLocation = useLocation();
	const { search } = pathLocation;
	const clientId = utils.getClientId(search);
	const code = utils.getSearchValue(search, "code");
	const campaignId = utils.getChallengeId(search);
	const href = convertURL(pathLocation).toLowerCase();

	useScrollHandler(wrapperRef);

	useEffect(() => {
		if (!googleAppData.selectedMediaList.length) {
			setActiveUploaderType(""); // stop interval in google photos
		}
	}, [googleAppData]);

	const socialSource = useMemo(() => {
		return {
			isTikTok: uploadSourceType === UPLOADER_TYPE.TIKTOK,
			isInstagram: uploadSourceType === UPLOADER_TYPE.INSTAGRAM,
		};
	}, [uploadSourceType]);

	const isWidget = useMemo(() => {
		return widgetInfo && !isFailed && !isShowLoading;
	}, [widgetInfo, isShowLoading, isFailed]);

	useEffect(() => {
		if (createContentStatus === SUCCEEDED && createdContent) {
			setIsShowLoading(false);
			uploadContent(createdContent);
		}
	}, [createContentStatus]);

	useEffect(() => {
		if (uploadSocialContentStatus === SUCCEEDED) {
			handleFormState([]);
			if (activeUploaderType === UPLOADER_TYPE.GOOGLE_DRIVE) {
				setGoogleAppData(DEFAULT_GOOGLE_DRIVE_REQUEST_DATA);
			}
		}

		if (uploadSocialContentStatus === FAILED && uploadSocialContentError) {
			setIsShowLoading(false);
			handleErrorMsg(uploadSocialContentError);
		}

		if (uploadSocialContentStatus === FAILED || uploadSocialContentStatus === SUCCEEDED) {
			setIsShowLoading(false);
			dispatch(
				storeData({
					uploadSocialContentStatus: NONE,
					uploadSocialContentError: null,
				}),
			);
		}
	}, [uploadSocialContentStatus, uploadSocialContentError]);

	useEffect(() => {
		if (googleApiError) {
			handleErrorMsg(googleApiError);
		}
	}, [googleApiError]);

	const { isInProgress, isCompleteUpload, isInError } = useUploadProgress();

	useEffect(() => {
		if (isCompleteUpload) {
			if (uploadedContentListResult.length) {
				handleSendEmail();
			}
			setIsCancelUpload(false);
		}
	}, [isCompleteUpload, uploadedContentListResult]);

	useEffect(() => {
		if (isCompleteUpload) {
			// setTimeout is used to user checking error
			setTimeout(
				() => {
					if (uploadedContentListResult.length && !isCancelledProgressModal) {
						resetDataInStore();
						handleFormState(uploadedContentListResult);
					}
				},
				fileProgress.some((item) => item.limitationError) ? DURATION.S_3 : DURATION.S_0,
			);
		}
	}, [isCompleteUpload, isCancelledProgressModal]);

	const handleSendEmail = () => {
		const payloadSendEmail = {
			email: payload?.emailAddress,
			requestIds: [],
			termsIds: getTermsIds(terms),
			widgetUrl: window.location.href,
		};
		if (uploadedContentListResult.length) {
			payloadSendEmail.requestIds = uploadedContentListResult.map((item) => item.requestId);
		}
		if (campaignId) {
			payloadSendEmail.campaignId = campaignId;
		}
		dispatch(sendEmailRequest(payloadSendEmail));
	};

	const onUploadProgress = async (params) => {
		const { progress, data, fileProgressId, requestId } = params;
		const { loaded, total } = progress;
		const percentageProgress = Math.floor((loaded / total) * MAX_PERCENT);
		const dataObject = utils.convertFormDataToObject(data);
		const endTime = new Date().getTime();
		const duration = (endTime - dataObject.startTime) / 1000;
		const bps = loaded / duration;
		const time = (total - loaded) / bps;

		const payload = {
			...dataObject,
			fileProgressId,
			progress: percentageProgress,
			remainingTime: time,
			requestId,
		};

		dispatch(setUploadProgress(payload));
	};

	const uploadContent = (createdContent) => {
		const contentList = convertUploadPayload(
			createdContent,
			widgetInfo,
			campaignId,
			uploadPayloadList,
		);
		if (contentList.length)
			contentList.forEach((content) => {
				const dataObject = utils.convertFormDataToObject(content.formData);
				const errorPromise = handleFileError(dataObject.file, widgetInfo, t);
				errorPromise.then((result) => {
					if (!_.isEmpty(result)) {
						const { progress = 1, fileProgressId, searchParams } = content;
						const payload = {
							...dataObject,
							fileProgressId,
							progress,
							remainingTime: 0,
							requestId: searchParams?.requestId,
							limitationError: result,
							status: FAILED,
						};
						dispatch(setUploadProgress(payload));
					} else {
						dispatch(
							uploadContentRequest({
								...content,
								onUploadProgress,
								widgetInfo,
								isNewFlow: widgetInfo?.widgetSettingsDTO?.isNewFlow,
							}),
						);
					}
				});
			});
	};

	const handleFormState = (result) => {
		setUploadResults(result);
		setFormState(2);
		dispatch(
			storeData({
				isCancelledProgressModal: true,
			}),
		);
	};

	const handleUploadMore = () => {
		setFormState(1);
		setActiveUploaderType("");
		resetData();
		resetDataInStore();
	};

	const className = search.includes(SCRIPT_TAG) ? SCRIPT_TAG : "";

	const getUploadResults = () => {
		if (activeUploaderType === UPLOADER_TYPE.YOUTUBE) {
			return uploadSocialContentResult;
		}

		return uploadedContentListResult.length ? uploadedContentListResult : uploadResults;
	};

	const renderWidget = () => {
		let { metadata = "" } = widgetInfo;

		if (metadata) {
			const {
				bgUrl = "",
				bgColor = "",
				formBackground = THEME.colors.white,
				highlight,
			} = metadata.colors;
			const { srcLogo = "", sizeLogo = {} } = metadata.header;

			if (formState === 1) {
				return (
					<StyledWrapperDetailWidget
						tabIndex={-1}
						ref={wrapperRef}
						bgUrl={getUrlS3(bgUrl)}
						bgColor={bgColor}
						formBackground={formBackground}
						className={className}>
						<StyledDetailWidget bgColor={formBackground} isMobile={isMobile}>
							<WidgetHeader sizeLogo={sizeLogo} srcLogo={getUrlS3(srcLogo)} />
							<WidgetBody
								detailWidget={widgetInfo}
								isMoreField={isMoreField}
								setIsMoreField={setIsMoreField}
								globalProps={globalProps}
								handleFormState={handleFormState}
							/>
							<WidgetFooter
								highlight={highlight}
								handleUploadLimitTerms={handleUploadLimitTerms}
								formState={formState}
							/>
						</StyledDetailWidget>
					</StyledWrapperDetailWidget>
				);
			}

			return (
				<UploaderConfirmation
					isMobile={isMobile}
					uploadResults={getUploadResults()}
					metaData={metadata}
					handleUploadMore={handleUploadMore}
					activeUploaderType={activeUploaderType}
					className={className}
				/>
			);
		}
	};

	const handleUploadLimitTerms = () => {
		setIsUploadLimitTerms(!isUploadLimitTerms);
	};

	const renderDefaultWidget = () => {
		if (clientId) {
			return <Default globalProps={globalProps} />;
		}

		return <NotFound globalProps={globalProps} />;
	};

	const resetData = () => {
		setSelectedVideoList([]);
		setGoogleAppData({ selectedMediaList: [], accessToken: "" });
	};

	const resetDataInStore = () => {
		const resetValues = {
			createdContent: null,
			createContentStatus: NONE,
			isCancelledProgressModal: true,
			uploadSocialContentResult: [],
			fileProgress: [],
		};

		dispatch(storeData(resetValues));
	};

	const confirmCancel = () => {
		if (fileProgress.length) {
			fileProgress.forEach((item) => {
				if (item.status === IN_PROGRESS)
					dispatch(
						cancelRequest({
							fileProgressId: item.fileProgressId,
							isNotRemove: true,
							actionType: LIMITATION_ERROR_TYPE.CANCEL,
						}),
					);
			});
		}
		resetData();
		resetDataInStore();
		setIsCancelUpload(false);
	};

	const onCancelUpload = () => {
		if (isInProgress && !isInError) {
			setIsCancelUpload(true);
		} else {
			resetDataInStore();
		}
	};

	const handleErrorMsg = (error) => {
		setErrorMsg(error);

		setTimeout(() => {
			setErrorMsg("");
			if (googleApiError) {
				dispatch(
					storeGoogleDataRequest({
						error: null,
					}),
				);
			}
		}, DURATION.S_10);
	};

	const renderSocialComponents = () => {
		const { colors } = widgetInfo.metadata || {};
		const { enableSharing, enableSharingYouTube, enableSharingInstagram } = colors || {};

		return (
			<>
				{enableSharing && (
					<WidgetTikTok
						isMobile={isMobile}
						videoList={videoList}
						setVideoList={setVideoList}
						uploadInfo={payload}
						setUploadInfo={setPayload}
						widgetInfo={widgetInfo}
						modalType={modalType}
						setModalType={setModalType}
						socialSource={socialSource}
						setTikTokUser={setTikTokUser}
						setSelectedVideoList={setSelectedVideoList}
					/>
				)}
				{enableSharingYouTube && (
					<WidgetYouTube
						widgetInfo={widgetInfo}
						isMobile={isMobile}
						setSelectedVideoList={setSelectedVideoList}
						setModalType={setModalType}
						modalType={modalType}
					/>
				)}
				{enableSharingInstagram && <WidgetInstagram isMobile={isMobile} />}
			</>
		);
	};

	const contextValues = {
		widgetInfo,
		videoList,
		setVideoList,
		selectedVideoList,
		setSelectedVideoList,
		tikTokUser,
		setTikTokUser,
		activeUploaderType,
		setActiveUploaderType,
		isCancelUpload,
		setIsCancelUpload,
		resetDataInStore,
		setUploadPayloadList,
		uploadInfo: payload,
		setUploadInfo: setPayload,
		isMobile,
		formState,
		className,
		googleAppData,
		setGoogleAppData,
		modalType,
		setModalType,
		setIsShowLoading,
		errorMsg,
		setErrorMsg,
		handleErrorMsg,
		terms,
		setTerms,
		socialSource,
	};

	return (
		<WidgetBuilderContext.Provider value={contextValues}>
			{isWidget && renderWidget()}
			{!isWidget && renderDefaultWidget()}

			{!!fileProgress.length && !isCancelledProgressModal && (
				<UploadProgress
					visible={!!fileProgress.length && !isCancelledProgressModal}
					onCancelUpload={onCancelUpload}
					widgetInfo={widgetInfo}
				/>
			)}

			{widgetInfo && (
				<CancelUpload
					visible={isCancelUpload}
					onConfirm={confirmCancel}
					bgSubmit={widgetInfo.metadata.colors.bgSubmit}
					onCancel={() => {
						setIsCancelUpload(false);
					}}
				/>
			)}

			{widgetInfo && isUploadLimitTerms && (
				<UploadLimitTerms
					widgetDetail={widgetInfo}
					onClose={handleUploadLimitTerms}
					isMobile={isMobile}
				/>
			)}

			{widgetInfo && renderSocialComponents()}
		</WidgetBuilderContext.Provider>
	);
};

WidgetBuilder.propTypes = {
	requestAPI: PropTypes.func,
	globalProps: PropTypes.object,
	isShowLoading: PropTypes.bool,
	widgetInfo: PropTypes.object,
};

export default WidgetBuilder;
